import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PublicLayout from "@/components/PublicLayout";
import { LockKey, ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (next !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      toast.success("Password updated — you can sign in now.");
    } catch (e2) {
      setError(e2.response?.data?.detail || "Could not reset password.");
    } finally { setBusy(false); }
  };

  return (
    <PublicLayout>
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-5 py-14">
        <div className="rounded-2xl border border-border bg-white p-7 shadow-lg">
          <div className="flex items-center gap-2 text-[#2D6A4F]">
            <ShieldCheck size={20} weight="fill" />
            <p className="overline text-[10px]">Account security</p>
          </div>
          <h1 className="mt-1 font-display text-2xl font-black tracking-tight">Choose a new password</h1>

          {!token ? (
            <p className="mt-4 text-sm text-red-600" data-testid="reset-no-token">
              This reset link is missing its token. Please request a new link from the sign-in page.
            </p>
          ) : done ? (
            <div className="mt-6 text-center" data-testid="reset-done">
              <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-100 text-[#2D6A4F]">
                <CheckCircle size={24} weight="fill" />
              </div>
              <p className="mt-3 text-sm text-zinc-700">Your password has been updated.</p>
              <Button data-testid="reset-go-login" onClick={() => nav("/login")}
                className="mt-4 h-11 w-full rounded-md bg-[#F27A54] font-semibold text-white hover:bg-[#d9633f]">
                Go to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="reset-form">
              <div>
                <Label htmlFor="rp-next">New password</Label>
                <div className="relative mt-1.5">
                  <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="rp-next" type="password" autoComplete="new-password" data-testid="reset-new"
                    value={next} onChange={(e) => setNext(e.target.value)} placeholder="At least 8 characters" className="h-11 pl-9" />
                </div>
              </div>
              <div>
                <Label htmlFor="rp-confirm">Confirm new password</Label>
                <Input id="rp-confirm" type="password" autoComplete="new-password" data-testid="reset-confirm"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-type new password" className="mt-1.5 h-11" />
              </div>
              {error && <p data-testid="reset-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
              <Button data-testid="reset-submit" type="submit" disabled={busy || !next || !confirm}
                className="h-11 w-full gap-2 rounded-md bg-[#2D6A4F] font-semibold text-white hover:bg-[#245840] disabled:opacity-60">
                <LockKey size={16} weight="bold" /> {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
